import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {
  getOrCreateTranslator,
  isTranslatorApiSupported,
  retryTranslator,
  translateContent,
  type DynamicTranslationCategory,
} from './content-translator.js';
import { LoadingScreen } from './components/loading-screen.js';
import { LANGUAGE_SELECTED_EVENT, useLanguage } from './language-context.js';

const GATE_MAX_MS = 20000;
const READY_SETTLE_MS = 50;

export type ContentTranslationStatus =
  'english' | 'unsupported' | 'preparing' | 'ready' | 'unavailable';

type TranslationPhase = 'preparing' | 'ready' | 'unavailable';

interface ContentTranslationValue {
  readonly status: ContentTranslationStatus;
  readonly targetLanguage: string;
  readonly track: (promise: Promise<unknown>) => void;
}

const ContentTranslationContext = createContext<ContentTranslationValue | null>(null);

function TranslationLoadingOverlay(): React.JSX.Element {
  return (
    <div data-testid="content-translation-gate" aria-busy="true">
      <LoadingScreen fullScreen />
    </div>
  );
}

export function ContentTranslationProvider({
  children,
}: {
  readonly children: ReactNode;
}): React.JSX.Element {
  const { language } = useLanguage();
  const apiSupported = isTranslatorApiSupported();
  const [phase, setPhase] = useState<TranslationPhase>('preparing');
  const [pending, setPending] = useState<ReadonlySet<symbol>>(new Set());
  const [settled, setSettled] = useState(false);
  const [forced, setForced] = useState(false);
  const [initialDone, setInitialDone] = useState(false);
  const [previousLanguage, setPreviousLanguage] = useState(language);

  if (previousLanguage !== language) {
    setPreviousLanguage(language);
    setPhase('preparing');
    setPending(new Set());
    setSettled(false);
    setForced(false);
    setInitialDone(false);
  }

  // Track the most recently selected language so a late-resolving retry for
  // an older selection cannot mark the wrong locale ready.
  const latestTargetRef = useRef(language);
  useEffect(() => {
    latestTargetRef.current = language;
  }, [language]);

  // Explicit dropdown selection is the transient user activation — retry
  // creation synchronously inside the event dispatch so a pair that failed
  // on refresh (NotAllowedError) gets a fresh chance.
  useEffect(() => {
    const onSelected = (event: Event): void => {
      const target = (event as CustomEvent<string>).detail;
      if (typeof target !== 'string' || target === 'en' || !apiSupported) return;
      latestTargetRef.current = target;
      void retryTranslator(target).then((translator) => {
        // Same-language re-selection does not re-run the prepare effect, so
        // apply the activation-time retry result directly.
        if (latestTargetRef.current === target) {
          setPhase(translator === null ? 'unavailable' : 'ready');
        }
      });
    };
    window.addEventListener(LANGUAGE_SELECTED_EVENT, onSelected);
    return () => {
      window.removeEventListener(LANGUAGE_SELECTED_EVENT, onSelected);
    };
  }, [apiSupported]);

  // Automatic preparation: on mount (restored locale) and on locale change.
  // If the browser refuses create() without activation this fails safe to
  // 'unavailable' and the original API values render.
  useEffect(() => {
    if (language === 'en' || !apiSupported) return;
    let live = true;
    void getOrCreateTranslator(language).then((translator) => {
      if (live) setPhase(translator === null ? 'unavailable' : 'ready');
    });
    return () => {
      live = false;
    };
  }, [language, apiSupported]);

  // Grace window after 'ready' so freshly mounted hooks can register their
  // pending translations before the gate releases.
  useEffect(() => {
    if (phase !== 'ready' || settled) return;
    const timer = setTimeout(() => {
      setSettled(true);
    }, READY_SETTLE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [phase, settled]);

  // Bounded readiness window — the gate can never hold the app indefinitely.
  const gatedForTimer = language !== 'en' && apiSupported && !forced && !initialDone;
  useEffect(() => {
    if (!gatedForTimer) return;
    const timer = setTimeout(() => {
      setForced(true);
    }, GATE_MAX_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [gatedForTimer]);

  // Once the initial preparation completes, later navigations translate in
  // place from the warm cache instead of re-gating the whole app.
  useEffect(() => {
    if (initialDone) return;
    if (phase !== 'unavailable' && !(phase === 'ready' && settled && pending.size === 0)) {
      return;
    }
    const timer = setTimeout(() => {
      setInitialDone(true);
    }, 0);
    return () => {
      clearTimeout(timer);
    };
  }, [phase, settled, pending, initialDone]);

  const track = useCallback((promise: Promise<unknown>): void => {
    const token = Symbol('translation');
    setPending((tokens) => new Set(tokens).add(token));
    void promise.finally(() => {
      setPending((tokens) => {
        const next = new Set(tokens);
        next.delete(token);
        return next;
      });
    });
  }, []);

  const status: ContentTranslationStatus =
    language === 'en' ? 'english' : !apiSupported ? 'unsupported' : phase;

  const gated =
    language !== 'en' &&
    apiSupported &&
    !forced &&
    !initialDone &&
    (phase === 'preparing' || (phase === 'ready' && (!settled || pending.size > 0)));

  const value = useMemo<ContentTranslationValue>(
    () => ({ status, targetLanguage: language, track }),
    [status, language, track],
  );

  return (
    <ContentTranslationContext.Provider value={value}>
      {children}
      {gated && <TranslationLoadingOverlay />}
    </ContentTranslationContext.Provider>
  );
}

/**
 * Returns `source` immediately; once content translation is enabled and the
 * translation resolves, returns the translated string. Safe outside the
 * provider and for every failure path — the API value is always the floor.
 */
export function useTranslatedText(
  category: DynamicTranslationCategory,
  source: string | null | undefined,
): string {
  const ctx = useContext(ContentTranslationContext);
  const text = source ?? '';
  const enabled = ctx?.status === 'ready';
  const targetLanguage = ctx?.targetLanguage ?? 'en';
  const scope = `${targetLanguage}|${category}|${text}`;
  const [value, setValue] = useState(text);
  const [previousScope, setPreviousScope] = useState(scope);

  if (previousScope !== scope) {
    setPreviousScope(scope);
    setValue(text);
  }

  useEffect(() => {
    if (!enabled || text === '') return;
    let live = true;
    const request = translateContent(category, targetLanguage, text);
    ctx.track(request);
    void request.then((translated) => {
      if (live) setValue(translated);
    });
    return () => {
      live = false;
    };
  }, [enabled, targetLanguage, category, text, ctx]);

  return value;
}

const LIST_SEPARATOR = '\u0001';

/** Batch variant of {@link useTranslatedText} for lists such as amenities. */
export function useTranslatedTexts(
  category: DynamicTranslationCategory,
  sources: readonly string[],
): readonly string[] {
  const ctx = useContext(ContentTranslationContext);
  const enabled = ctx?.status === 'ready';
  const targetLanguage = ctx?.targetLanguage ?? 'en';
  const signature = sources.join(LIST_SEPARATOR);
  const scope = `${targetLanguage}|${category}|${signature}`;
  const [translated, setTranslated] = useState<ReadonlyMap<string, string>>(new Map());
  const [previousScope, setPreviousScope] = useState(scope);

  if (previousScope !== scope) {
    setPreviousScope(scope);
    setTranslated(new Map());
  }

  useEffect(() => {
    const current = signature === '' ? [] : signature.split(LIST_SEPARATOR);
    if (!enabled || current.length === 0) return;
    let live = true;
    const request = Promise.all(
      current.map((text) => translateContent(category, targetLanguage, text)),
    );
    ctx.track(request);
    void request.then((results) => {
      if (!live) return;
      setTranslated((prev) => {
        const next = new Map(prev);
        current.forEach((text, index) => {
          next.set(text, results[index] ?? text);
        });
        return next;
      });
    });
    return () => {
      live = false;
    };
  }, [enabled, targetLanguage, category, signature, ctx]);

  return sources.map((text) => translated.get(text) ?? text);
}

/** Renderless wrapper for JSX sites that cannot host a hook call. */
export function DynamicText({
  category,
  source,
}: {
  readonly category: DynamicTranslationCategory;
  readonly source: string | null | undefined;
}): React.JSX.Element {
  return <>{useTranslatedText(category, source)}</>;
}
